import type { DTOMediaInfo } from './DTOMediaInfo';

export const DTO_MESSAGE_INFO_MESSAGE_CONTENT_TYPES = {
  /** 비디오콜 결과 상대로부터 응답이 없었을 때 messageContent 에 들어가는 문자열. */
  VIDEO_CALL_NOT_RESPONDED: '<<☎>> NOT_RESPONDED',

  /** 비디오콜을 상대방이 받은 상태인 경우 messageContent 에 들어가는 문자열. */
  VIDEO_CALL_ANSWERED: '<<☎>> ANSWERED',

  /** 비디오콜이 정상적으로 끝났을 때 messageContent 에 들어가는 문자열의 prefix. 이 뒤로 통화시간이 "hh:mm:ss" 형식으로 붙는다.*/
  VIDEO_CALL_FINISHED_PREFIX: '<<☎>> FINISHED ',

  /** 비디오콜 결과 발신자가 취소한 경우 messageContent 에 들어가는 문자열. */
  VIDEO_CALL_CANCELED: '<<☎>> CANCELED',

  /** 비디오콜 결과 상대방이 거부한 경우 messageContent 에 들어가는 문자열. */
  VIDEO_CALL_DECLINED: '<<☎>> DECLINED',

  /** 이미지 메시지인 경우 messageContent 에 들어가는 문자열. */
  IMAGE: '<<🖼>>', // U+1F5BC, picture with frame,

  /** 번역이 실패했을 경우 translatedMessage 에 들어가는 문자열. */
  TRANSLATION_FAILED: '__TRANSLATION_FAILED__',
} as const;

export const DTO_MESSAGE_INFO_SYSTEM_MESSAGE_TYPES = [
  /** system message type friend request. */
  'friendRequestedByUser',

  /** system message type friend request. */
  'friendRequestedByPeer',

  /** system message type friend accept. */
  'friendAcceptByUser',

  /** system message type friend accept. */
  'friendAcceptByPeer',

  /** system message type content image. */
  'contentImageSent',

  /** system message type content image. */
  'contentImageReceived',
] as const;

export type DTOMessageInfoSystemMessageType =
  (typeof DTO_MESSAGE_INFO_SYSTEM_MESSAGE_TYPES)[number];

/**
 * 대화방 내에서 주고받아진 한 건의 메시지를 나타내는 DTO.
 *
 * 타이틀, 본문, 이미지, 버튼으로 구성되며 각각의 요소는 모두 optional이다.
 */
export interface DTOMessageInfo {
  /** 해당 대화방에서 본 메시지의 일련번호. 큰 값일수록 최신 메시지이다 */
  messageSeqNo: number;

  /** 내가 보낸 메시지인지 여부. */
  sentByMe: boolean;

  /** 본 메시지가 전송된(=서버에서 요청을 수신한) 시각. */
  dateMessageSent: number;

  /**
   * 메시지 본문.
   * messageContent 가 현재는 NotNull 이나 구버전 호환을 위함.
   */
  messageContent?: string;

  /**
   * 메시지 타이틀. 없는 경우 null. 이 값이 존재하는 경우 클라이언트는 알림창에 메시지 내용 대신 타이틀을 표시하여야 한다.
   * @since 164
   */
  title?: string;

  /**
   * 이미지 정보. 없는 경우 null.
   * @since 164
   */
  mediaInfo?: DTOMediaInfo;

  /**
   * 버튼 캡션. 없는 경우 null.
   * @since 164
   */
  buttonCaption?: string;

  /**
   * 클릭시 실행할 링크 URL. 없는 경우 null.
   * @since 164
   */
  linkUrl?: string;

  /**
   * 비디오콜인 상태 등의 정보. 비디오콜인 경우에만 존재. 이 값이 존재하는 경우에는 비디오콜 메시지이므로
   * 이미지, 버튼, 타이틀 등의 값은 의미가 없다.
   * @since 164
   */
  videoCallStatus?: unknown;

  /**
   * 시스템 메시지인 경우 true.
   * @since 190
   */
  systemMessage?: boolean;

  /**
   * 번역된 메시지의 경우, 번역된 문자열. 번역 에러인 경우에는 {@link #MESSAGE_CONTENT_TRANSLATION_FAILED}
   * @since 240
   */ /** 번역된 메시지 (번역 실패 시 '__TRANSLATION_FAILED__' 값이 들어감) */
  translatedMessage?: string;

  /**
   * 시스템 메시지인 경우 타입.
   * 일반 메시지인 경우 null.
   *
   * @since 630
   */
  systemMessageType?: string;

  /**
   * 메세지 유효기간.
   *
   * 현재는 봇 메시지에만 서버에서 내려준 메세지 유효기간을 적용하고 있다. 봇 메세지 판단은 {@link FriendInfo#getFriendType()}
   * 또는 {@link FriendListItem#getFriendType()}이 "OFFICIAL" 일 때 봇 메시지로 판단하고 나머지는 일반 메세지로 판단한다.
   *
   * @since 2250
   */
  dateExpires?: number;
}
